import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Trash2, FileStack } from "lucide-react";
import { formatMoney, SALES_DOCUMENT_TYPE_LABELS } from "@finora/config";
import type { ProductDto, SalesDocumentType } from "@finora/types";
import { Button, EmptyState } from "@finora/ui";
import { api } from "../../lib/api";
import { ApiError } from "@finora/api-client";
import { ProductAutocomplete } from "../../components/ProductAutocomplete";
import { CustomerAutocomplete } from "../../components/CustomerAutocomplete";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";

/**
 * Isti ekran služi i za KREIRANJE nove ponude/fakture (bez `id` u ruti -
 * "/prodaja/nova-ponuda") i za IZMENU postojeće (sa `id` -
 * "/prodaja/:id/izmeni"). Tabela stavki je namerno modelovana po Zoho
 * Invoice obrascu: izbor artikla odmah dodaje red u tabelu, a
 * količina/cena/popust/porez se posle menjaju direktno u tom redu
 * (autosave na izlasku iz polja), bez posebnog "Dodaj" koraka.
 */
const NUMBER_FORMAT = new Intl.NumberFormat("sr-RS", { maximumFractionDigits: 2 });

/** Procenat popusta -> fiksni iznos popusta, na osnovu trenutne količine i cene stavke. */
function discountPercentToAmount(percent: string, quantity: string, unitPriceGross: string): string {
  const pct = Number(percent) || 0;
  const grossBeforeDiscount = Number(quantity) * Number(unitPriceGross);
  return ((grossBeforeDiscount * pct) / 100).toFixed(2);
}

/** Fiksni iznos popusta -> procenat, za prikaz u polju (obrnuto od gornje funkcije). */
function discountAmountToPercent(discountAmount: string, quantity: string, unitPriceGross: string): string {
  const grossBeforeDiscount = Number(quantity) * Number(unitPriceGross);
  if (grossBeforeDiscount <= 0) return "0";
  return NUMBER_FORMAT.format((Number(discountAmount) / grossBeforeDiscount) * 100);
}

export function SalesDocumentEditorPage({ type }: { type?: SalesDocumentType }) {
  const params = useParams<{ id?: string }>();
  const isEditMode = !!params.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [documentId, setDocumentId] = useState<string | null>(params.id ?? null);
  const [pendingCustomerId, setPendingCustomerId] = useState("");

  const { data: dashboard } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.getDashboard(),
    enabled: !isEditMode,
  });
  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: () => api.listCustomers(),
  });
  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const { data: taxRates } = useQuery({
    queryKey: ["tax-rates"],
    queryFn: () => api.listTaxRates(),
  });
  const { data: document, isLoading: isDocumentLoading } = useQuery({
    queryKey: ["sales-document", documentId],
    queryFn: () => api.getSalesDocument(documentId!),
    enabled: !!documentId,
  });

  const createDocument = useMutation({
    mutationFn: () =>
      api.createSalesDocument({
        type: type!,
        businessLocationId: dashboard!.defaultBusinessLocationId,
        customerId: pendingCustomerId,
      }),
    onSuccess: (doc) => setDocumentId(doc.id),
  });

  // Kreiranje dokumenta ide isključivo na eksplicitan klik "Nastavi" (ispod,
  // u "Faza 1" prikazu) - ne automatski čim se izabere kupac, da ne bi bilo
  // iznenađenja/duplog kreiranja.

  const updateDocument = useMutation({
    mutationFn: (patch: Parameters<typeof api.updateSalesDocument>[1]) =>
      api.updateSalesDocument(documentId!, patch),
    onSuccess: (doc) => queryClient.setQueryData(["sales-document", doc.id], doc),
  });

  const addItem = useMutation({
    mutationFn: (product: ProductDto) =>
      api.addSalesDocumentItem(documentId!, {
        productId: product.id,
        name: product.name,
        quantity: "1",
        unitPriceGross: product.unitPriceGross,
        taxRateId: product.taxRateId,
        discountAmount: "0",
        purchasePriceGross: product.effectivePurchasePrice ?? null,
      }),
    onSuccess: (doc) => queryClient.setQueryData(["sales-document", doc.id], doc),
  });

  const updateItem = useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: {
        quantity?: string;
        discountAmount?: string;
        unitPriceGross?: string;
        taxRateId?: string;
        serialNumber?: string | null;
      };
    }) => api.updateSalesDocumentItem(documentId!, itemId, patch),
    onSuccess: (doc) => queryClient.setQueryData(["sales-document", doc.id], doc),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.removeSalesDocumentItem(documentId!, itemId),
    onSuccess: (doc) => queryClient.setQueryData(["sales-document", doc.id], doc),
  });

  const send = useMutation({
    mutationFn: async () => {
      // Fakture (za razliku od ponuda) moraju prvo u status "Odobreno" pre
      // slanja (DRAFT -> APPROVED -> SENT) - ponude idu direktno DRAFT -> SENT.
      if (!isEditMode && type !== "QUOTE") {
        await api.approveSalesDocument(documentId!);
      }
      return api.sendSalesDocument(documentId!);
    },
    onSuccess: (doc) => navigate(`/prodaja/${doc.id}`),
  });

  if (isEditMode && isDocumentLoading) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  // Faza 1: dok korisnik ne izabere kupca, dokument se uopšte ne kreira -
  // korisnik je tražio da se bez kupca ne može ni napraviti nacrt.
  if (!isEditMode && !documentId) {
    const preTypeLabel = type ? SALES_DOCUMENT_TYPE_LABELS[type] : "";
    return (
      <div className="mx-auto max-w-md space-y-5">
        <h1 className="text-xl font-semibold text-slate-900">
          Nova {preTypeLabel.toLowerCase()}
        </h1>
        <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
          <label className="mb-1 block text-sm font-medium text-red-600">Kupac *</label>
          <CustomerAutocomplete
            value={customers?.find((c) => c.id === pendingCustomerId)?.name ?? ""}
            onSelect={(customer) => setPendingCustomerId(customer.id)}
          />
          <p className="mt-2 text-xs text-slate-500">
            Kupac je obavezan pre nego što se kreira dokument.
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => navigate("/prodaja")}>
            Otkaži
          </Button>
          <Button disabled={!pendingCustomerId || createDocument.isPending} onClick={() => createDocument.mutate()}>
            Nastavi
          </Button>
        </div>
      </div>
    );
  }

  const docType = document?.type ?? type;
  const typeLabel = docType ? SALES_DOCUMENT_TYPE_LABELS[docType] : "";
  const isQuote = docType === "QUOTE";

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <h1 className="text-xl font-semibold text-slate-900">
        {isEditMode ? `Izmena - ${typeLabel}` : `Nova ${typeLabel.toLowerCase()}`}
      </h1>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="grid grid-cols-1 gap-5 border-b border-border p-5 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-red-600">Kupac *</label>
            <CustomerAutocomplete
              value={customers?.find((c) => c.id === document?.customerId)?.name ?? ""}
              onSelect={(customer) =>
                documentId && updateDocument.mutate({ customerId: customer.id })
              }
            />
            <p className="mt-1 text-xs text-slate-400">
              Vi ste {companySettings?.isVatRegistered ? "u sistemu PDV-a" : "van sistema PDV-a"}
              {document?.customerId && (
                <>
                  {" "}
                  · Kupac je{" "}
                  {customers?.find((c) => c.id === document.customerId)?.isVatRegistered
                    ? "obveznik PDV-a"
                    : "nije obveznik PDV-a"}
                </>
              )}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-500">Broj dokumenta</label>
            <input
              disabled
              value={document?.documentNumber ?? "(dodeljuje se prilikom slanja)"}
              className="h-10 w-full rounded-lg border border-border bg-app-bg px-3 text-sm text-slate-400"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-red-600">Datum izdavanja *</label>
            <DateInputDDMMYYYY
              value={document?.issueDate ?? new Date().toISOString()}
              onChange={(iso) => documentId && updateDocument.mutate({ issueDate: iso })}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              {isQuote ? "Rok važenja" : "Rok plaćanja"}
            </label>
            <DateInputDDMMYYYY
              value={document?.validUntilOrDueDate}
              onChange={(iso) =>
                documentId && updateDocument.mutate({ validUntilOrDueDate: new Date(iso).toISOString() })
              }
            />
          </div>
        </div>

        <div className="p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">Stavke</h2>

          <table>
            <thead>
              <tr>
                <th className="uppercase tracking-wide">Naziv artikla</th>
                <th className="w-20 uppercase tracking-wide">Kol.</th>
                <th className="w-28 uppercase tracking-wide">Cena</th>
                <th className="w-24 uppercase tracking-wide">Popust %</th>
                <th className="w-36 uppercase tracking-wide">Porez</th>
                <th className="w-28 text-right uppercase tracking-wide">Iznos</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {document?.items.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium text-slate-800">
                    {item.name}
                    {item.purchasePriceGross && (() => {
                      const qty = Number(item.quantity) || 1;
                      const effectiveUnitPrice =
                        Number(item.unitPriceGross) - Number(item.discountAmount) / qty;
                      const margin = effectiveUnitPrice - Number(item.purchasePriceGross);
                      return (
                        <div className="mt-0.5 text-xs font-normal text-slate-400">
                          Nabavna: {formatMoney(item.purchasePriceGross)} · Razlika u ceni:{" "}
                          <span className={margin < 0 ? "text-red-500" : "text-emerald-600"}>
                            {formatMoney(margin.toFixed(2))}/kom
                          </span>
                        </div>
                      );
                    })()}
                    <input
                      defaultValue={item.serialNumber ?? ""}
                      placeholder="Serijski broj (opciono)"
                      onBlur={(e) =>
                        updateItem.mutate({
                          itemId: item.id,
                          patch: { serialNumber: e.target.value || null },
                        })
                      }
                      className="mt-1 h-7 w-48 rounded border border-transparent bg-transparent px-1 text-xs text-slate-400 hover:border-border focus:border-brand focus:bg-surface focus:outline-none"
                    />
                  </td>
                  <td>
                    <input
                      defaultValue={item.quantity}
                      onBlur={(e) => {
                        if (e.target.value && e.target.value !== item.quantity) {
                          updateItem.mutate({ itemId: item.id, patch: { quantity: e.target.value } });
                        }
                      }}
                      className="h-8 w-full rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </td>
                  <td>
                    <input
                      defaultValue={item.unitPriceGross}
                      onBlur={(e) => {
                        if (e.target.value && e.target.value !== item.unitPriceGross) {
                          updateItem.mutate({
                            itemId: item.id,
                            patch: { unitPriceGross: e.target.value },
                          });
                        }
                      }}
                      className="h-8 w-full rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </td>
                  <td>
                    <div className="relative">
                      <input
                        defaultValue={discountAmountToPercent(
                          item.discountAmount,
                          item.quantity,
                          item.unitPriceGross,
                        )}
                        onBlur={(e) => {
                          const nextAmount = discountPercentToAmount(
                            e.target.value || "0",
                            item.quantity,
                            item.unitPriceGross,
                          );
                          if (nextAmount !== item.discountAmount) {
                            updateItem.mutate({
                              itemId: item.id,
                              patch: { discountAmount: nextAmount },
                            });
                          }
                        }}
                        className="h-8 w-full rounded-md border border-border pl-2 pr-6 text-sm focus:border-brand focus:outline-none"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                        %
                      </span>
                    </div>
                  </td>
                  <td>
                    <select
                      defaultValue={
                        taxRates?.find((t) => t.ratePercent === item.taxRatePercent)?.id ?? ""
                      }
                      onChange={(e) =>
                        e.target.value &&
                        updateItem.mutate({ itemId: item.id, patch: { taxRateId: e.target.value } })
                      }
                      className="h-8 w-full rounded-md border border-border px-1 text-sm focus:border-brand focus:outline-none"
                    >
                      {taxRates?.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.ratePercent}%
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="text-right font-medium">{formatMoney(item.grossAmount)}</td>
                  <td>
                    <button
                      aria-label="Ukloni stavku"
                      onClick={() => documentId && removeItem.mutate(item.id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={7} className="bg-app-bg/50">
                  <ProductAutocomplete
                    placeholder="Ukucaj ili klikni da izabereš artikal..."
                    onSelect={(product) => documentId && addItem.mutate(product)}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {/*
            RANIJE se greška dodavanja stavke (npr. backend odbija sa "Iznos
            mora biti veći od nule" kad artikal nema podešenu cenu) nigde nije
            prikazivala - padajuća lista se zatvarala kao da je izbor uspeo, a
            stavka se nikad nije pojavila u tabeli, bez ikakvog traga zašto
            (vidi razgovor "kucam nadjem proizvod ali ne mogu da ga
            selektujem" / "nista se ne doda samo nestane"). Isti obrazac kao
            send.isError ispod.
          */}
          {addItem.isError && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {addItem.error instanceof ApiError
                ? addItem.error.response.message
                : "Dodavanje stavke nije uspelo. Pokušajte ponovo."}
            </p>
          )}

          {(!document || document.items.length === 0) && (
            <div className="mt-3">
              <EmptyState
                icon={FileStack}
                title="Nema dodatih stavki"
                description="Izaberite artikal u polju iznad da dodate prvu stavku."
              />
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Osnovica</span>
                <span>{formatMoney(document?.netAmount ?? "0")}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>PDV</span>
                <span>{formatMoney(document?.taxAmount ?? "0")}</span>
              </div>
              {Number(document?.discountAmount ?? "0") > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Popust</span>
                  <span>-{formatMoney(document!.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-1 text-base font-semibold text-slate-900">
                <span>Ukupno</span>
                <span>{formatMoney(document?.grossAmount ?? "0")}</span>
              </div>
            </div>

            {document && (() => {
              const itemsWithCost = document.items.filter((item) => item.purchasePriceGross);
              if (itemsWithCost.length === 0) return null;
              const totalMargin = itemsWithCost.reduce((sum, item) => {
                const qty = Number(item.quantity) || 1;
                const effectiveUnitPrice = Number(item.unitPriceGross) - Number(item.discountAmount) / qty;
                return sum + (effectiveUnitPrice - Number(item.purchasePriceGross)) * qty;
              }, 0);
              return (
                <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2">
                  <div className="flex justify-between text-sm font-medium text-amber-800">
                    <span>Ukupna razlika u ceni (marža)</span>
                    <span className={totalMargin < 0 ? "text-red-600" : ""}>
                      {formatMoney(totalMargin.toFixed(2))}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-amber-700">
                    Samo za tebe - ne šalje se kupcu i ne prikazuje se na PDF-u/štampi.
                    {itemsWithCost.length < document.items.length &&
                      ` (${document.items.length - itemsWithCost.length} stavki nema poznatu nabavnu cenu, nisu uračunate)`}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 border-t border-border p-5 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Napomena kupcu</label>
            <textarea
              defaultValue={document?.customerNote ?? ""}
              onBlur={(e) => documentId && updateDocument.mutate({ customerNote: e.target.value })}
              rows={3}
              placeholder="npr. Hvala Vam na poverenju."
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Uslovi poslovanja</label>
            <textarea
              defaultValue={document?.paymentTerms ?? ""}
              onBlur={(e) => documentId && updateDocument.mutate({ paymentTerms: e.target.value })}
              rows={3}
              placeholder="npr. Uplata 100% avansno, rok isporuke 3-5 radnih dana."
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end gap-2">
        {send.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {send.error instanceof ApiError
              ? send.error.response.message
              : "Slanje nije uspelo. Pokušajte ponovo."}
          </p>
        )}
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate(isEditMode ? `/prodaja/${documentId}` : "/prodaja")}>
            {isEditMode ? "Nazad na pregled" : "Otkaži"}
          </Button>
          {!isEditMode && (
            <Button
              disabled={!document || document.items.length === 0 || send.isPending}
              onClick={() => send.mutate()}
            >
              {send.isPending ? "Šaljem..." : "Sačuvaj i pošalji"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
